import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import LoadState from '@/plugins/load-state';
import api from '@/common/api';
import sessionStore from '@/common/storage/session-store';
import app from '@/store/app';
import router from '@/store/router';
import user, {persistedStateUserKey} from '@/store/user';
import rulesEngineStore from '@/modules/rules-engine/rules-engine-store';
import intercomManager from '@/vendor/intercom';
import intercomStore from '@/store/modules/intercom-store';
import gtmStore from '@/store/modules/gtm-store';
import fullStory from '@/vendor/fullstory';
import jobsStore from '@/store/jobs';
import toast from '@/common/toast';
import auth from '@/common/auth/auth';
import config from '@/common/config';
import StoreModules from '@/store/modules';
import {resetStoreMutation, resetStorePlugin} from '@/plugins/reset-store';

const {
    VUE_APP_MODE: mode,
    VUE_APP_API_URL: apiUrl,
    VUE_APP_DEBUG: strict,
    VUE_APP_USE_WEB_STORAGE_FOR_JWT: storeJwt,
} = config;

const loadStateManager = new LoadState();

// inject plugins
Vue.use(Vuex);
Vue.use(loadStateManager);

const modules = {
    app: app({
        apiHandler: api,
        sessionStore,
        fullStory,
        toast,
        auth,
        isSandbox: `${mode}` === 'sandbox',
        isLocal: apiUrl.includes('.dev-local'),
    }),
    router: router(api),
    user: user({apiHandler: api, auth, storeJwt}),
    rulesEngine: rulesEngineStore({apiHandler: api, Vue}),
    jobs: jobsStore(api),

    intercom: intercomStore({intercomManager}),
    gtm: gtmStore({dataLayer: window.dataLayer}),
};

const storeModules = new StoreModules({
    apiHandler: api,
    context: {
        modules: require.context('../modules', true, /^.*\/store\/.+\.js$/),
        components: require.context(`../components`, true, /^.*\/store\/.+\.js$/),
    },
    protectedNamespaces: Object.keys(modules),
});

export default new Vuex.Store({
    // actions,
    // getters,
    modules: {
        ...modules,
        ...storeModules.modules,
    },
    strict,
    plugins: [
        // Warning: the order in which plugins are executed
        // directly influences the end result of the store!
        // -------------------------------------------------

        // clear modules store on user logout
        // that plugin should be the first plugin in the list
        // this plugin uses the initial store as restored value
        // so if any plugin modify store before that plugin we will have incorrect behavior
        resetStorePlugin(['user/reset']),

        // synchronize loading state for multiple components
        loadStateManager.store,

        // persist the user data into localStorage
        // for login usage and preferences
        createPersistedState({
            key: persistedStateUserKey,
            paths: ['user'],
        }),

        // persist the router data into the `sessionStore`
        // for redirects after login or session expired
        createPersistedState({
            storage: sessionStorage,
            key: 'reb.app.route',
            paths: ['router'],
        }),

        createPersistedState({
            key: 'reb.app.jobs',
            paths: ['jobs'],
        }),
    ],
    mutations: {
        // reset-store root mutation
        // the mutation which will reset the store values
        // based on the mutation names list in the resetStorePlugin
        resetStoreMutation,
    },
});
